import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import { Calendar, Video, Clock, Star, Gift, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth, API, SHOW_PRICING } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const Coaching = () => {
  const { user, token } = useAuth();
  const [bookingStatus, setBookingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCalendly, setShowCalendly] = useState(false);
  
  // During beta (SHOW_PRICING=false), treat everyone as beta user
  const isBetaUser = !SHOW_PRICING || user?.is_beta_user || user?.subscription_status === 'beta';

  // Fetch booking status on mount
  useEffect(() => {
    const fetchBookingStatus = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/coaching/booking-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBookingStatus(response.data);
      } catch (error) {
        console.error('Error fetching booking status:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBookingStatus();
  }, [token]);

  // Load Calendly widget script
  useEffect(() => {
    if (showCalendly) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);
      
      return () => {
        const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
        if (existingScript) {
          document.body.removeChild(existingScript);
        }
      };
    }
  }, [showCalendly]);

  // Listen for Calendly events to capture booking
  useEffect(() => {
    const handleMessage = async (e) => {
      if (e.data.event && e.data.event.indexOf('calendly') === 0) {
        if (e.data.event === 'calendly.event_scheduled') {
          // User booked a session via Calendly
          try {
            const eventData = e.data.payload;
            await axios.post(
              `${API}/coaching/book-session`,
              {
                booking_date: eventData?.event?.start_time?.split('T')[0] || new Date().toISOString().split('T')[0],
                booking_time: eventData?.event?.start_time?.split('T')[1]?.substring(0, 5) || '00:00',
                timezone: eventData?.event?.timezone || 'Europe/Zurich',
                notes: 'Booked via Calendly'
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setBookingStatus({
              has_booking: true,
              can_book: false,
              message: 'Session réservée avec succès !'
            });
            
            toast.success('Session réservée avec succès !');
          } catch (error) {
            console.error('Error recording booking:', error);
          }
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [token]);

  const handleShowCalendly = () => {
    if (bookingStatus?.has_booking) {
      toast.error('Vous avez déjà utilisé votre session gratuite');
      return;
    }
    setShowCalendly(true);
  };

  return (
    <DashboardLayout title="Réserver une Session Coaching">
      <div className="max-w-5xl mx-auto">
        {/* Hero Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-luxury p-8 mb-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
                <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">COACHING PRIVÉ</span>
              </div>
              <h2 className="font-serif text-3xl text-white mb-4">Session Individuelle avec Manuela</h2>
              <p className="text-[#71717A] mb-6 leading-relaxed font-light">
                Un accompagnement personnalisé pour transformer ta vision en réalité. 
                Réserve ta session privée gratuite de 45 minutes pour travailler ta stratégie de marque.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <Video className="text-[#D4AF37]" size={18} strokeWidth={1.5} />
                  <span className="text-[#A1A1AA] font-light">Appel vidéo via Zoom</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="text-[#D4AF37]" size={18} strokeWidth={1.5} />
                  <span className="text-[#A1A1AA] font-light">Session de 45 minutes</span>
                </div>
                <div className="flex items-center gap-3">
                  <Star className="text-[#D4AF37]" size={18} strokeWidth={1.5} />
                  <span className="text-[#A1A1AA] font-light">Plan d'action personnalisé inclus</span>
                </div>
                <div className="flex items-center gap-3">
                  <Gift className="text-[#D4AF37]" size={18} strokeWidth={1.5} />
                  <span className="text-[#E5C585] font-medium">1 session offerte pendant la bêta</span>
                </div>
              </div>

              {/* Booking Status & CTA */}
              {loading ? (
                <div className="flex items-center gap-3 text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Chargement...</span>
                </div>
              ) : bookingStatus?.has_booking ? (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-3 text-green-400 mb-2">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-medium">Session déjà réservée</span>
                  </div>
                  {bookingStatus.booking && (
                    <p className="text-gray-400 text-sm">
                      Date : {bookingStatus.booking.date} à {bookingStatus.booking.time}
                    </p>
                  )}
                  <p className="text-gray-500 text-xs mt-2">
                    Vous avez utilisé votre session gratuite de 45 minutes.
                  </p>
                </div>
              ) : (
                <Button
                  onClick={handleShowCalendly}
                  className="btn-gold px-8 py-4 text-lg"
                  data-testid="book-session-btn"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Réserver ma session offerte
                </Button>
              )}
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1734341739615-71be2b1cdb006?w=400&h=500&fit=crop"
                alt="Manuela Clierxter"
                className="w-full aspect-[4/5] object-cover border border-[#27272A]"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-[#050505]/90 backdrop-blur-sm p-4 border border-[#D4AF37]/20">
                <p className="font-serif text-white">Manuela Clierxter</p>
                <p className="text-[#D4AF37] text-xs font-accent tracking-[0.15em]">BRAND VISIONARY</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Calendly Widget - Only shown after clicking the button */}
        {showCalendly && !bookingStatus?.has_booking && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.1 }} 
            className="card-premium p-8 mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Calendar className="text-[#E5C585]" size={24} />
                <h3 className="font-serif text-xl text-white">Choisis un créneau</h3>
              </div>
              <Button 
                variant="ghost" 
                onClick={() => setShowCalendly(false)}
                className="text-gray-400 hover:text-white"
              >
                Fermer
              </Button>
            </div>
            
            <div className="bg-[#0A0A0A] border border-[#1C1C1C] rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 text-[#E5C585]">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">Cette session gratuite de 45 minutes ne peut être réservée qu'une seule fois.</span>
              </div>
            </div>
            
            <div 
              className="calendly-inline-widget" 
              data-url="https://calendly.com/manuelaclierxter/45min?hide_gdpr_banner=1&background_color=050505&text_color=ffffff&primary_color=d4af37"
              style={{ minWidth: '320px', height: '700px' }}
            />
          </motion.div>
        )}

        {/* What to Expect */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.2 }} 
          className="card-premium p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
            <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">CE QUI T'ATTEND</span>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 bg-[#0A0A0A] border border-[#1C1C1C] rounded-xl">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-4">
                <span className="text-[#D4AF37] font-serif text-xl">1</span>
              </div>
              <h4 className="font-serif text-lg text-white mb-2">Clarification</h4>
              <p className="text-gray-500 text-sm leading-relaxed">
                On clarifie ta vision, ton positionnement et ce qui te rend unique.
              </p>
            </div>
            
            <div className="p-6 bg-[#0A0A0A] border border-[#1C1C1C] rounded-xl">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-4">
                <span className="text-[#D4AF37] font-serif text-xl">2</span>
              </div>
              <h4 className="font-serif text-lg text-white mb-2">Stratégie</h4>
              <p className="text-gray-500 text-sm leading-relaxed">
                On définit ensemble les actions prioritaires pour ta marque.
              </p>
            </div>
            
            <div className="p-6 bg-[#0A0A0A] border border-[#1C1C1C] rounded-xl">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-4">
                <span className="text-[#D4AF37] font-serif text-xl">3</span>
              </div>
              <h4 className="font-serif text-lg text-white mb-2">Plan d'action</h4>
              <p className="text-gray-500 text-sm leading-relaxed">
                Tu repars avec un plan clair et des prochaines étapes concrètes.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default Coaching;
